import type { TFunction } from 'i18next';
import type { Delivery, Supplier } from '../interfaces/ILogistics.js';
import { carriedNote } from './carriedNote.js';
import { addDays, formatDay } from './format.js';
import {
  precedentFor,
  type IOrderPrecedent,
  type IPrefillSource,
  type OrderPrefill,
} from './orderPrefill.js';
import type { IDeliveryLineDraft } from './deliveryLines.js';
import { supplierById } from './suppliers.js';

/**
 * What the order dialog suggests for the WHOLE order, and why — re-derived
 * from the set of materials on it every time a row is picked or removed
 * (ruling 2026-09-15).
 *
 * The rule the whole module turns on: destination, supplier, ETA and note are facts
 * about a TRUCK, not about a material. With one material on the order the two
 * are the same thing, so that material's own history answers all four. Add a
 * second and they stop being the same thing — where Ready-mix usually goes
 * says nothing about where a truck carrying Ready-mix AND rebar should go —
 * so the answer has to come from a delivery that actually carried this
 * combination, or from nothing at all.
 *
 * Empty is a real answer here, not a failure to compute one. The footnote is
 * what makes it usable: it hands over the per-material facts (each one's last
 * destination, each one's own lead) so the user can decide, instead of
 * presenting an averaged figure nobody has ever observed.
 */
export interface IOrderSuggestion {
  destinationName: string;
  dueDate: string;
  note: string;
  /** The suggested supplier's id, or '' for none — '' rather than null so it
   *  re-seeds through the same `reseed` as the text fields. */
  supplierId: string;
  /** The one-line reason under each field, already translated — '' when there
   *  is nothing to explain. Rendered while the field still holds the
   *  suggestion (an EMPTY suggestion in an empty field included: "not
   *  suggested, and here is why" is the point of the no-precedent case). */
  destinationWhy: string;
  supplierWhy: string;
  dueWhy: string;
  noteWhy: string;
  /** The lead time behind `dueDate` — what the form's too-soon check measures
   *  against. Null when no figure stands behind the date. */
  leadTimeDays: number | null;
}

/** Nothing suggested and nothing to explain: a blank order, a row typed by
 *  hand, or an EDIT (whose values are the user's own saved answers). */
export const EMPTY_ORDER_SUGGESTION: IOrderSuggestion = {
  destinationName: '',
  dueDate: '',
  note: '',
  supplierId: '',
  destinationWhy: '',
  supplierWhy: '',
  dueWhy: '',
  noteWhy: '',
  leadTimeDays: null,
};

export interface ISuggestOrderInput {
  /** The materials table as it stands. Rows with no material name yet are
   *  ignored — an empty row is not a third material. */
  lines: IDeliveryLineDraft[];
  /** The site's delivery history, for the combination lookup. */
  deliveries: Delivery[];
  /** The site's suppliers, to name each material's own in a footnote. */
  suppliers: Supplier[];
  /** The PROJECT's zone — which calendar day a past arrival landed on. */
  tz: string;
  /** Today in that zone, passed in so one render and the handler that follows
   *  it cannot straddle midnight and disagree. */
  today: string;
  /** The viewer's language, for the list joins in the footnotes. */
  lang: string;
  t: TFunction;
}

/** The dot that already separates facts on the board's cards. */
const SEPARATOR = ' · ';
const KEY = 'logistics.orderDialog.provenance';

export function suggestOrder(input: ISuggestOrderInput): IOrderSuggestion {
  const named = input.lines.filter((line) => line.itemName.trim() !== '');
  if (named.length === 0) return EMPTY_ORDER_SUGGESTION;
  if (named.length === 1) return fromOneMaterial(named[0]!, input.lang, input.t);
  return fromSeveralMaterials(named, input);
}

/**
 * One material: its own history IS the truck's history, so this is the
 * prefill the pick (or "Order now") already carried, unchanged.
 */
function fromOneMaterial(line: IDeliveryLineDraft, lang: string, t: TFunction): IOrderSuggestion {
  const prefill = line.prefill;
  // Typed by hand rather than picked: the system inferred nothing, so it has
  // nothing to mark and nothing to explain.
  if (prefill === null) return EMPTY_ORDER_SUGGESTION;
  const dueDate = prefill.dueDate ?? '';
  const lead = prefill.leadTimeDays ?? null;
  const destinationName = prefill.destinationName ?? '';
  const supplierId = prefill.supplierId ?? '';
  return {
    destinationName,
    dueDate,
    note: prefill.deliveryOrder ?? '',
    supplierId,
    destinationWhy:
      destinationName === ''
        ? ''
        : carriedFootnote(line.itemName.trim(), prefill.destinationSource ?? null, lang, t),
    supplierWhy:
      supplierId === '' ? '' : supplierFootnote(line.itemName.trim(), prefill.supplierSource ?? null, lang, t),
    // A seeded date with no lead time behind it (a task hand-off's own
    // deadline) must not read "estimated to take d."
    dueWhy: dueDate === '' ? '' : leadFootnote(lead, t),
    noteWhy: '',
    leadTimeDays: lead,
  };
}

/** "Where {material} was last delivered · #{ref}, {day}" — the order the
 *  destination was read off, named (ruling 2026-09-15). An order that can date
 *  itself neither by arrival nor by request is unciteable, so the footnote
 *  falls back to the sentence that names none. */
function carriedFootnote(
  material: string,
  source: IPrefillSource | 'task' | null,
  lang: string,
  t: TFunction,
): string {
  // A Stock-tab hand-off: the task's own floor and zone, not a past delivery.
  if (source === 'task') return t(`${KEY}.destinationTask`);
  if (source === null) return t('logistics.orderDialog.destinationWhy');
  return t(`${KEY}.destinationCarried`, {
    material,
    ref: source.ref,
    day: formatDay(source.day, lang),
  });
}

/** "Who last supplied {material} · #{ref}, {day}" — the destination's
 *  footnote, for the truck's other habitual fact. */
function supplierFootnote(material: string, source: IPrefillSource | null, lang: string, t: TFunction): string {
  if (source === null) return t('logistics.orderDialog.supplierWhy');
  return t(`${KEY}.supplierCarried`, { material, ref: source.ref, day: formatDay(source.day, lang) });
}

function leadFootnote(lead: number | null, t: TFunction): string {
  return lead === null
    ? t('logistics.orderDialog.dueWhyUnknown')
    : t('logistics.orderDialog.dueWhy', { lead });
}

/**
 * Several materials: one truck, so the answer is the last truck that carried
 * exactly this set — or an explained blank.
 */
function fromSeveralMaterials(
  named: IDeliveryLineDraft[],
  { deliveries, suppliers, tz, today, lang, t }: ISuggestOrderInput,
): IOrderSuggestion {
  const materials = named.map((line) => ({ name: line.itemName.trim(), prefill: line.prefill }));
  const precedent = precedentFor(
    materials.map((m) => m.name),
    deliveries,
    tz,
  );
  /* One reorder line per material that has a stock row to reorder against —
     never one material's whole `deliveryOrder`, which would drag that
     material's last dispatch note in as if it applied to all of them. */
  const reorderLines = materials.flatMap((m) =>
    m.prefill?.reorderNote ? [m.prefill.reorderNote] : [],
  );
  /* A task hand-off's sentence heads the note ONCE, however many of the
     task's materials the truck carries — it is about the order, not a line. */
  const taskNotes = [...new Set(materials.flatMap((m) => (m.prefill?.taskNote ? [m.prefill.taskNote] : [])))];
  // The precedent truck's note, less the lines the system wrote for IT — its
  // own reorder reasons, its task lines — so only the instructions carry.
  const carried = precedent === null ? '' : carriedNote(precedent.note, t);
  const note = joinNote([...taskNotes, ...reorderLines], carried);
  const noteWhy = noteFootnote(note, carried, precedent, reorderLines.length === 0, t);
  // Every line raised from the same task's place (a Stock-tab checkout): the
  // truck goes where the task works — the answer one material from the task
  // gets too — not where a past truck of this set went, and not a blank.
  const taskPlace = sharedTaskPlace(materials);

  if (precedent !== null) {
    const supplierId = precedent.supplierId ?? '';
    return {
      destinationName: taskPlace ?? precedent.destinationName,
      dueDate: precedent.leadDays === null ? '' : addDays(today, precedent.leadDays),
      note,
      supplierId,
      supplierWhy:
        supplierId === ''
          ? ''
          : t(`${KEY}.supplierPrecedent`, { ref: precedent.ref, day: formatDay(precedent.deliveredDay, lang) }),
      destinationWhy: taskPlace !== null ? t(`${KEY}.destinationTask`) : t(`${KEY}.destinationPrecedent`, {
        ref: precedent.ref,
        day: formatDay(precedent.deliveredDay, lang),
      }),
      dueWhy:
        precedent.leadDays === null
          ? ''
          : t(`${KEY}.etaPrecedent`, { lead: precedent.leadDays, ref: precedent.ref }),
      noteWhy,
      leadTimeDays: precedent.leadDays,
    };
  }

  /* No precedent. The ETA is offered only when every material has a known
     lead and all of them are EQUAL (ruling 2026-09-15): then "today + lead"
     is what each of them says on its own and the footnote is the plain lead
     sentence. Any difference, or any unknown, leaves the ETA blank with each
     material's own lead listed — never the longest one, which would be an
     inference about how THIS supplier assembles a mixed load that two
     separate trucks cannot support. */
  const shared = sharedLead(materials);
  return {
    destinationName: taskPlace ?? '',
    dueDate: shared === null ? '' : addDays(today, shared),
    note,
    // Who supplies a mixed load is no more inferable than where it goes: the
    // pick stays blank, and the footnote lists each material's own supplier.
    supplierId: '',
    supplierWhy: noSupplierFootnote(materials, suppliers, t),
    destinationWhy: taskPlace !== null ? t(`${KEY}.destinationTask`) : noDestinationFootnote(materials, t),
    dueWhy: shared === null ? noEtaFootnote(materials, t) : leadFootnote(shared, t),
    noteWhy,
    // The too-soon check measures against the shared lead when there is one;
    // with none, any date the user picks is theirs to judge.
    leadTimeDays: shared,
  };
}

/** The task's place when EVERY material came from it (a Stock-tab hand-off
 *  marks its destination `'task'`) and they all name the same one; null
 *  otherwise. */
function sharedTaskPlace(materials: IMaterialFact[]): string | null {
  const places = materials.map((m) => (m.prefill?.destinationSource === 'task' ? m.prefill.destinationName ?? '' : ''));
  const first = places[0] ?? '';
  return first !== '' && places.every((place) => place === first) ? first : null;
}

/** The one lead every material agrees on, or null when any is unknown or
 *  any two differ. */
function sharedLead(materials: IMaterialFact[]): number | null {
  const leads = materials.map((m) => m.prefill?.leadTimeDays ?? null);
  const first = leads[0] ?? null;
  return first !== null && leads.every((lead) => lead === first) ? first : null;
}

/** The materials and their own facts, as the footnotes below list them. */
interface IMaterialFact {
  name: string;
  prefill: Partial<OrderPrefill> | null;
}

/** Which story the note tells: the combination's own note carried forward, a
 *  line written per material, or — with no reorder line at all — the task
 *  sentence the order is raised for. Nothing at all when the note is empty —
 *  there is then no value to explain. */
function noteFootnote(
  note: string,
  carried: string,
  precedent: IOrderPrecedent | null,
  taskOnly: boolean,
  t: TFunction,
): string {
  if (note === '') return '';
  if (precedent !== null && carried !== '') {
    return t(`${KEY}.notePrecedent`, { ref: precedent.ref });
  }
  return taskOnly ? t(`${KEY}.noteTask`) : t(`${KEY}.notePerMaterial`);
}

/** "Not ordered together before · {A} → {dest A} · {B} → {dest B}" — the
 *  per-material facts that replace a destination nobody can infer. Materials
 *  with no past destination are left out; with none at all, the bare phrase
 *  stands on its own rather than trailing an empty list. */
function noDestinationFootnote(materials: IMaterialFact[], t: TFunction): string {
  const went = materials.flatMap((m) => {
    const dest = m.prefill?.destinationName ?? '';
    return dest === '' ? [] : [t(`${KEY}.destWent`, { name: m.name, dest })];
  });
  if (went.length === 0) return t(`${KEY}.destinationNone`);
  return t(`${KEY}.destinationNoneWith`, { details: went.join(SEPARATOR) });
}

/** "Not ordered together before · {A} → {supplier A} · …" — the materials'
 *  own suppliers, the facts a blank pick hands over. Silent when none of them
 *  has a known supplier: the field is optional, and a footnote under every
 *  supplier-less mixed order would explain an absence nobody is missing. */
function noSupplierFootnote(materials: IMaterialFact[], suppliers: Supplier[], t: TFunction): string {
  const went = materials.flatMap((m) => {
    const known = supplierById(suppliers, m.prefill?.supplierId ?? null);
    // The destination's own phrases: the same "{A} → {B}" list under the
    // same "not ordered together" lead.
    return known === null ? [] : [t(`${KEY}.destWent`, { name: m.name, dest: known.name })];
  });
  return went.length === 0 ? '' : t(`${KEY}.destinationNoneWith`, { details: went.join(SEPARATOR) });
}

/** "Not ordered together before · {A} ~{leadA}d · {B} ~{leadB}d" — same
 *  shape, for the ETA. */
function noEtaFootnote(materials: IMaterialFact[], t: TFunction): string {
  const known = materials.flatMap((m) => {
    const lead = m.prefill?.leadTimeDays ?? null;
    return lead === null ? [] : [t(`${KEY}.leadAlone`, { name: m.name, lead })];
  });
  if (known.length === 0) return t(`${KEY}.etaNone`);
  return t(`${KEY}.etaNoneWith`, { leads: known.join(SEPARATOR) });
}

/**
 * The dispatch note for several materials: every material's own reorder line,
 * then the combination's last note after a blank line.
 *
 * The carried note is the site's standing instructions for this truck (gate,
 * crane, who to call) and stays true order to order; the reorder lines are
 * what changed, so they go first.
 */
function joinNote(reorderLines: string[], carried: string): string {
  const body = reorderLines.join('\n');
  if (carried === '') return body;
  return body === '' ? carried : `${body}\n\n${carried}`;
}

/**
 * The new suggestion, but only into a field the user has not made their own.
 *
 * A field still holding exactly what we last put there is ours to update; one
 * the user emptied is ours to fill again. Anything else is theirs — it keeps
 * the typed value, drops the amber mark, and the form's existing "Suggested X"
 * note shows what was offered instead.
 */
export function reseed(current: string, lastSuggested: string, next: string): string {
  return current === lastSuggested || current === '' ? next : current;
}

/** The last value the system actually OFFERED for each order-level field. */
